import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import InternationalMetaTagsManager from './InternationalMetaTagsManager';

const PrivacyPage: React.FC = () => {
  const { t } = useTranslation();
  
  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
  }, []);

  const formatDate = (date: Date): string => {
    return date.toLocaleDateString();
  };

  const lastUpdated = new Date('2024-01-01'); // Update this date when policy changes

  return (
    <>
      <InternationalMetaTagsManager pageType="privacy" />
      <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              {t('privacy.title')}
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t('privacy.subtitle')}
            </p>
            <p className="text-sm text-gray-500 mt-4">
              {t('privacy.lastUpdated', { date: formatDate(lastUpdated) })}
            </p>
          </div>

          {/* Introduction */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-8 mb-8">
            <div className="flex items-start space-x-4">
              <svg className="w-8 h-8 text-blue-600 flex-shrink-0 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              <div>
                <h2 className="text-2xl font-bold text-blue-900 mb-4">
                  {t('privacy.introTitle')}
                </h2>
                <p className="text-blue-800 text-lg leading-relaxed">
                  {t('privacy.introText')}
                </p>
              </div>
            </div>
          </div>

          {/* Data Collection Section */}
          <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              {t('privacy.dataCollectionTitle')}
            </h2>
            <p className="text-gray-700 text-lg leading-relaxed mb-6">
              {t('privacy.dataCollectionText')}
            </p>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-green-50 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-green-800 mb-3 flex items-center">
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  What We DON'T Collect
                </h3>
                <ul className="text-green-700 space-y-2">
                  <li>• Personal names or identities</li>
                  <li>• Email addresses or contact information</li>
                  <li>• IP addresses for reports</li>
                  <li>• User accounts or profiles</li>
                  <li>• Location tracking data</li>
                  <li>• Payment information</li>
                </ul>
              </div>
              
              <div className="bg-blue-50 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-blue-800 mb-3 flex items-center">
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  What We DO Collect
                </h3>
                <ul className="text-blue-700 space-y-2">
                  <li>• Anonymous report content</li>
                  <li>• Restaurant information (public data)</li>
                  <li>• Basic usage statistics (anonymized)</li>
                  <li>• Language preferences</li>
                  <li>• Technical error logs (no personal data)</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Anonymity Guarantee Section */}
          <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              {t('privacy.anonymityTitle')}
            </h2>
            <p className="text-gray-700 text-lg leading-relaxed mb-6">
              {t('privacy.anonymityText')}
            </p>
            
            <div className="bg-gray-50 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">How We Ensure Anonymity</h3>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <div className="bg-green-100 rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 mt-1">
                    <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900">No Registration Required</h4>
                    <p className="text-gray-600">Reports can be submitted without creating accounts or providing personal information.</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <div className="bg-green-100 rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 mt-1">
                    <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900">No IP Address Logging</h4>
                    <p className="text-gray-600">We do not log or store IP addresses associated with reports.</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <div className="bg-green-100 rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 mt-1">
                    <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900">Data Minimization</h4>
                    <p className="text-gray-600">We only collect the minimum information necessary for the service to function.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Data Use Section */}
          <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              {t('privacy.dataUseTitle')}
            </h2>
            <p className="text-gray-700 text-lg leading-relaxed mb-6">
              {t('privacy.dataUseText')}
            </p>
            
            <div className="space-y-4">
              <div className="border-l-4 border-blue-500 pl-4">
                <h3 className="font-semibold text-gray-900">Public Food Safety Information</h3>
                <p className="text-gray-600">Anonymous reports are used to provide food safety information to help others make informed dining decisions.</p>
              </div>
              
              <div className="border-l-4 border-green-500 pl-4">
                <h3 className="font-semibold text-gray-900">Service Improvement</h3>
                <p className="text-gray-600">Aggregated, anonymized usage data helps us improve the platform's functionality and user experience.</p>
              </div>
              
              <div className="border-l-4 border-purple-500 pl-4">
                <h3 className="font-semibold text-gray-900">Legal Compliance</h3>
                <p className="text-gray-600">We may be required to provide anonymized data to health authorities for public safety purposes.</p>
              </div>
            </div>
          </div>

          {/* Cookies Section */}
          <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              {t('privacy.cookiesTitle')}
            </h2>
            <p className="text-gray-700 text-lg leading-relaxed mb-6">
              {t('privacy.cookiesText')}
            </p>
            
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-yellow-800 mb-3">Essential Cookies Only</h3>
              <p className="text-yellow-700">
                We use only essential cookies required for the website to function properly, such as language preferences and basic security measures. We do not use tracking cookies, advertising cookies, or analytics cookies that could compromise your anonymity.
              </p>
            </div>
          </div>

          {/* Contact Section */}
          <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              {t('privacy.contactTitle')}
            </h2>
            <p className="text-gray-700 text-lg leading-relaxed mb-6">
              {t('privacy.contactText')}
            </p>
            
            <div className="bg-gray-50 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-3">Your Rights</h3>
              <p className="text-gray-600 mb-4">
                Since we don't collect personal data, traditional data protection rights (like data access or deletion) don't apply. However, you have the right to:
              </p>
              <ul className="text-gray-600 space-y-2">
                <li>• Use our service completely anonymously</li>
                <li>• Submit reports without providing personal information</li>
                <li>• Request information about our privacy practices</li>
                <li>• Report any privacy concerns to us</li>
              </ul>
            </div>
          </div>

          {/* Legal Information */}
          <div className="bg-gray-100 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-3">Legal Basis</h3>
            <p className="text-gray-600 text-sm">
              This privacy policy is designed to comply with the General Data Protection Regulation (GDPR) and Belgian privacy laws. Our commitment to anonymity means that most GDPR provisions regarding personal data do not apply to our service, as we do not process personal data in the traditional sense.
            </p>
          </div>

          {/* Keywords for SEO */}
          <div className="hidden">
            <span>privacy policy</span>
            <span>anonymous reporting</span>
            <span>data protection</span>
            <span>food safety privacy</span>
            <span>GDPR compliance</span>
            <span>anonymous food reports</span>
            <span>privacy protection Belgium</span>
          </div>
        </div>
      </div>
    </>
  );
};

export default PrivacyPage;